<script setup lang="ts">
import { computed, useSlots } from 'vue';

import PhxsgPictureContent, {
  PhxPictureContentProps,
} from '@sg-core/src/View/Components/Atoms/Picture/phxsg-picture-content.vue';

export interface Picture {
  sources: {
    srcset: { path: string; size?: string }[] | string;
    width: number;
    height: number;
    media?: string;
  }[];
  image: {
    src: string;
    srcset?: string;
    width: number;
    height: number;
  };
}

export interface PhxPictureProps extends PhxPictureContentProps {
  actionable?: boolean;
  actionAttrs?: Record<string, unknown>;
  pictureAttrs?: Record<string, unknown>;
  href?: string;
  rootClass?: string;
  type?: 'default' | 'cover' | 'contain' | 'full';
  rootStyle?: string;
}

const {
  actionable = false,
  rootClass,
  type = 'default',
  rootStyle,
  href,
  alt,
  title,
  image,
  imgClass,
  lazy,
  fade = undefined,
  sources,
  pictureAttrs,
} = defineProps<PhxPictureProps>();

const emit = defineEmits<{
  (e: 'load', ev: Event): void;
  (e: 'click'): void;
}>();

const slots = useSlots();

const hasActionSlot = computed(() => Boolean(slots.action));
const hasVariantSlot = computed(() => Boolean(slots.variant));

const rootTag = computed<'div' | 'a' | 'button'>(() => {
  if (actionable && !hasActionSlot.value) {
    return href ? 'a' : 'button';
  }
  return 'div';
});

const pictureContentAttrs = computed(() => ({
  alt,
  title,
  image,
  imgClass,
  lazy,
  fade,
  sources,
  ...pictureAttrs,
}));

function handleLoaded(e: Event) {
  emit('load', e);
}

function handleClick() {
  if (actionable) {
    emit('click');
  }
}
</script>

<template>
  <component
    :is="rootTag"
    :class="[
      'phxsg-picture',
      {
        'phxsg-picture--cover': type === 'cover',
        'phxsg-picture--contain': type === 'contain',
        'phxsg-picture--full': type === 'full' || (type === 'default' && hasActionSlot),
        'phxsg-picture--actionable': actionable && !hasActionSlot,
        relative: hasActionSlot,
      },
      rootClass,
    ]"
    :href="href"
    :type="rootTag === 'button' && 'button'"
    :style="rootStyle"
    v-bind="{ ...$attrs, ...actionAttrs }"
    @click="handleClick"
  >
    <phxsg-picture-content v-bind="pictureContentAttrs" @load="handleLoaded" />

    <slot v-if="hasVariantSlot" name="variant" />

    <div v-if="hasActionSlot" class="phxsg-picture__wrapper__action">
      <slot name="action" />
    </div>
  </component>
</template>
